import withLifecycles from 'async-lifecycles';
import produce from 'immer';
import React, { Component, Fragment } from 'react';
import { PortalWithState } from 'react-portal';
import { Spring } from 'react-spring/renderprops';
import {
  Button,
  Header,
  Icon,
  Table,
  Popup,
  Label,
  Input,
  Pagination,
  Item,
} from 'semantic-ui-react';
import { Form } from 'semantic-ui-react';
import toastr from 'toastr';
import { connect } from 'react-redux';
import { includes, debounce } from 'lodash';

import { DateFormat, TimeFormat } from '.././components/date';
import Blacklist from '../../store/services/blacklist';
import searchImage from '../assets/search.svg';
import usersApi from '../../store/services/users';
import FormContainer from '../form-components/FormContainer';
import Layout from '../layout/Layout';
import LoadingContainer from '../layout/LoadingContainer';
import Page from '../layout/Page';
import { ModalWrapper } from '../modal/ModalWrapper';
import UserForm from './UserForm';
import RequiresRole, { ROLES } from '../security/RequiresRole';
import { ErrorMessages } from '../form-components/ErrorMessages';
import styled from 'react-emotion';

const Styles = styled.div`
  .tableHeader {
    background-color: '#f00';
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    justify-content: space-between;

    .ui.header {
      margin-bottom: 0;
    }
  }
`;

class AllUsersTable extends Component {
  state = {
    loading: false,
    pageInfo: {},
    user: undefined,
    orderByName: false,
    activePage: 1,
    searchTerm: '',
  };

  _mutate = cb => {
    this.setState(produce(cb));
  };

  componentDidMount() {
    this._loadUsers(0, '');
  }

  _deleteUser = async user => {
    try {
      await usersApi.remove(user);
      toastr.info('User removed');
      this._loadUsers(0, '');
      this.setState({ searchTerm: '' });
      this._closePortal();
    } catch (error) {
      toastr.warning('Error removing user');
    }
  };

  _blacklist = user => {
    user.isBlacklisted = true;
    this.setState({ user: user });

    withLifecycles(
      () => usersApi.update(this.state.user),
      ({ data, error }) => {
        this.setState(
          produce(draft => {
            draft.error = error;

            if (data && !data.hasErrors) {
              this.props.history.push('/manage/users');
              toastr.success('User Blacklisted');
              this._loadUsers(0, '');
              this.setState({ searchTerm: '' });
              this._closePortal();

              Blacklist.blacklist({
                ...user,
                userId: user.id,
                customer: user.customerName || user.company,
              });
            }
          })
        );
      }
    );
  };

  _output = () => {
    try {
      usersApi.export().then(response => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'users.csv');
        document.body.appendChild(link);
        link.click();

        toastr.info('CSV Downloaded');

        this._loadUsers(0, '');
        this.setState({ searchTerm: '' });
        this._closePortal();
      });
    } catch (error) {
      toastr.warning('Error downloading CSV');
    }
  };

  _handleTextChanged = (event, { name, value }) => {
    const shouldClearCustomer = !includes(
      [ROLES.CUSTOMER_ADMIN, ROLES.CUSTOMER_USER],
      value
    );

    this._mutate(draft => {
      draft.user[name] = value;

      if (name === 'role' && shouldClearCustomer) {
        draft.user.customerId = undefined;
      }
    });
  };

  _loadUsers = (pageNumber, searchTerm) => {
    withLifecycles(
      () =>
        usersApi.fetch({
          request: {
            page: pageNumber,
            pageSize: 20,
          },
          searchTerm: searchTerm,
          orderByName: this.state.orderByName,
        }),
      ({ loading, data, error }) => {
        this.setState({ activePage: pageNumber + 1 });
        this.setState(
          produce(draft => {
            draft.loading = loading;

            if (data && !data.hasErrors) {
              draft.pageInfo = data.result;
            }
          })
        );
      }
    );
  };

  _save = () => {
    withLifecycles(
      () => usersApi.update(this.state.user),
      ({ loading, data, error }) => {
        this.setState(
          produce(draft => {
            draft.saving = loading;
            draft.error = error;

            if (data && !data.hasErrors) {
              this.props.history.push('/manage/users');
              toastr.success('User Updated');
              this._loadUsers(0, '');
              this.setState({ searchTerm: '' });
              this._closePortal();
            }
          })
        );
      }
    );
  };

  _showDetail = user => {
    this._openPortal();
    this.setState({ user, error: {} });
  };

  render() {
    const { pageInfo, searchTerm, orderByName, activePage } = this.state;
    const users = pageInfo.data || [];
    return (
      <FormContainer loading={this.state.loading}>
        <Styles>
          <div className="tableHeader">
            <Header as="h1">Users</Header>
            <div>
              <RequiresRole
                roles={[
                  ROLES.MM_ADMIN,
                  ROLES.SYSTEM_ADMIN,
                  ROLES.CUSTOMER_ADMIN,
                ]}
              >
                <Button
                  primary
                  type="button"
                  style={{ marginRight: 8 }}
                  onClick={this._output}
                >
                  <Icon name="download" />
                  Export
                </Button>
              </RequiresRole>
              <Input
                placeholder="Search Users..."
                onChange={debounce((e, searchTerm) => {
                  this._loadUsers(0, searchTerm.value);
                  this.setState({ searchTerm: searchTerm.value });
                }, 400)}
              />
            </div>
          </div>
        </Styles>
        {pageInfo.pageCount > 0 && (
          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => {
              this._closePortal = closePortal;
              this._openPortal = openPortal;
              return (
                <Fragment>
                  <Table basic="very" selectable>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell />
                        <RequiresRole
                          roles={[ROLES.SYSTEM_ADMIN, ROLES.MM_ADMIN]}
                        >
                          <Table.HeaderCell
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              this.setState({ orderByName: false }, () =>
                                this._loadUsers(0, searchTerm)
                              );
                            }}
                          >
                            Customer {!orderByName && '▼'}
                          </Table.HeaderCell>
                        </RequiresRole>
                        <Table.HeaderCell
                          style={{ cursor: 'pointer' }}
                          onClick={() =>
                            this.setState({ orderByName: true }, () => {
                              this._loadUsers(0, searchTerm);
                            })
                          }
                        >
                          Full Name {orderByName && '▼'}
                        </Table.HeaderCell>
                        <Table.HeaderCell>Title</Table.HeaderCell>
                        <Table.HeaderCell>Email Address</Table.HeaderCell>
                        <Table.HeaderCell>Phone Number</Table.HeaderCell>
                        <Table.HeaderCell>Access</Table.HeaderCell>
                        <Table.HeaderCell>Registration Date</Table.HeaderCell>
                        <Table.HeaderCell>Verification Date</Table.HeaderCell>
                        <Table.HeaderCell>Last Login</Table.HeaderCell>
                        <Table.HeaderCell>Last Search</Table.HeaderCell>
                        <Table.HeaderCell>Last PDF Download</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {users.map(user => this._renderRow(user))}
                    </Table.Body>
                  </Table>
                  <Pagination
                    activePage={activePage}
                    onPageChange={async (e, paginationInfo) => {
                      await this._loadUsers(
                        paginationInfo.activePage - 1,
                        this.state.searchTerm
                      );
                    }}
                    ellipsisItem={{
                      content: <Icon name="ellipsis horizontal" />,
                      icon: true,
                    }}
                    firstItem={{
                      content: <Icon name="angle double left" />,
                      icon: true,
                    }}
                    lastItem={{
                      content: <Icon name="angle double right" />,
                      icon: true,
                    }}
                    prevItem={{ content: <Icon name="angle left" />, icon: true }}
                    nextItem={{
                      content: <Icon name="angle right" />,
                      icon: true,
                    }}
                    totalPages={pageInfo.pageCount}
                  />
                  {portal(this._renderDetailModal())}
                </Fragment>
              );
            }}
          </PortalWithState>
        )}
        {users.length === 0 && (
          <Item.Group>
            <Item>
              <Item.Image size="mini" src={searchImage} />
              <Item.Content verticalAlign="middle">
                <strong>No Users.</strong>
                <div>
                  <small>
                    No users match your search results. Please clear or refine
                    your search.
                  </small>
                </div>
              </Item.Content>
            </Item>
          </Item.Group>
        )}
      </FormContainer>
    );
  }

  _renderRow = user => (
    <Table.Row key={user.id}>
      <Table.Cell collapsing>
        <Button className="clear" icon onClick={() => this._showDetail(user)}>
          <Icon name="edit" />
        </Button>
      </Table.Cell>
      <RequiresRole roles={[ROLES.SYSTEM_ADMIN, ROLES.MM_ADMIN]}>
        <Table.Cell>
          {(user.customerName && (
            <div>
              {user.customerName}
              <Label
                size="mini"
                style={{
                  backgroundColor: '#e8c164',
                  color: '#FFF',
                  marginLeft: '5px',
                }}
              >
                Customer
              </Label>
            </div>
          )) ||
            (user.company && (
              <div>
                {user.company}
                <Label
                  size="mini"
                  style={{
                    backgroundColor: '#37817a',
                    color: '#FFF',
                    marginLeft: '5px',
                  }}
                >
                  From User
                </Label>
              </div>
            )) ||
            'N/A'}
        </Table.Cell>
      </RequiresRole>
      <Table.Cell>{user.fullName}</Table.Cell>
      <Table.Cell>{user.title}</Table.Cell>
      <Table.Cell>{user.emailAddress}</Table.Cell>
      <Table.Cell>{user.phoneNumber}</Table.Cell>
      <Table.Cell>{user.role}</Table.Cell>
      <Table.Cell>
        <DateFormat date={user.createdDate}></DateFormat>
      </Table.Cell>
      <Table.Cell>
        {user.verificationDate ? (
          <DateFormat date={user.verificationDate}></DateFormat>
        ) : (
            '--'
          )}
      </Table.Cell>
      <Table.Cell>
        {user.lastLogin ? (
          <TimeFormat date={user.lastLogin}></TimeFormat>
        ) : (
            '--'
          )}
      </Table.Cell>
      <Table.Cell>
        {user.lastSearchDate ? (
          <TimeFormat date={user.lastSearchDate}></TimeFormat>
        ) : (
            '--'
          )}
      </Table.Cell>
      <Table.Cell>
        {user.lastPdfDownload ? (
          <TimeFormat date={user.lastPdfDownload}></TimeFormat>
        ) : (
            '--'
          )}
      </Table.Cell>
    </Table.Row>
  );

  _renderDetailModal = () => {
    const { user, error = {} } = this.state;
    const customerOptions = this.props.customers.map(x => ({
      text: x.name,
      value: x.id,
    }));
    return (
      <Spring from={{ right: '-100%' }} to={{ right: '0%' }}>
        {styles => (
          <ModalWrapper style={styles}>
            <Layout className="page-wrapper">
              <Header className="page-header">
                <Page.HeaderWithActions title="Edit User">
                  <Button
                    icon="close"
                    size="mini"
                    onClick={this._closePortal}
                  />
                </Page.HeaderWithActions>
              </Header>
              <Layout.Fill scroll padded>
                <LoadingContainer hideContentWhenLoading>
                  <Form onSubmit={this._save}>
                    <UserForm
                      user={user}
                      role={this.props.user.role}
                      customerOptions={customerOptions}
                      handleTextChanged={this._handleTextChanged}
                    />
                    <ErrorMessages error={error} />
                    <Button type="Submit" primary loading={this.state.saving}>
                      Update
                    </Button>
                    <Button basic onClick={this._closePortal}>
                      Cancel
                    </Button>
                    {user && user.id !== parseInt(this.props.user.id, 10) && (
                      <Popup
                        trigger={
                          <Button basic icon floated="right" type="button">
                            <Icon name="trash" color="red" />
                          </Button>
                        }
                        content={
                          <Button
                            type="button"
                            color="red"
                            content="Are you sure?"
                            onClick={() => this._deleteUser(user)}
                          />
                        }
                        on="click"
                        position="left center"
                      />
                    )}

                    {user && user.id !== parseInt(this.props.user.id, 10) && (
                      <Popup
                        trigger={
                          <Button basic icon floated="right" type="button">
                            <Icon
                              style={{ paddingRight: '20px' }}
                              name="eye slash"
                              color="Black"
                            />
                            Blacklist
                          </Button>
                        }
                        content={
                          <Button
                            type="button"
                            color="red"
                            content="Are you sure?"
                            onClick={() => this._blacklist(user)}
                          />
                        }
                        on="click"
                        position="left center"
                      />
                    )}
                  </Form>
                </LoadingContainer>
              </Layout.Fill>
            </Layout>
          </ModalWrapper>
        )}
      </Spring>
    );
  };
}

const mapStateToProps = ({ customers, identity }) => ({
  customers: customers.data,
  user: identity.user,
});

export default connect(mapStateToProps)(AllUsersTable);
